import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2 } from 'lucide-react';
import { useChatbotStore } from '@store/slices/chatbot.slice';
import { chatbotService, ChatbotMessage } from '@services/api/chatbot.service';
import { useTenant } from '@/hooks/useTenant';
import ReactMarkdown from 'react-markdown';

export const ChatbotWidget: React.FC = () => {
  const { tenantInfo } = useTenant();
  const { isOpen, messages, isLoading, error, toggleChat, addMessage, setLoading, setError } =
    useChatbotStore();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const tenantName = tenantInfo?.name || 'Protectora';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage: ChatbotMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString(),
    };

    addMessage(userMessage);
    setInputValue('');
    setLoading(true);
    setError(null);

    try {
      const response = await chatbotService.sendMessage(
        userMessage.content,
        messages.filter((m) => m.role === 'user' || m.role === 'assistant'),
      );

      const assistantMessage: ChatbotMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: response.timestamp,
      };

      addMessage(assistantMessage);
    } catch (err: any) {
      setError(err?.message || 'Error al enviar el mensaje');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={toggleChat}
        className="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 z-50"
        aria-label="Abrir chat"
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-white dark:bg-gray-900 rounded-xl shadow-2xl flex flex-col overflow-hidden z-50 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between px-4 py-3 bg-blue-600 text-white">
        <div className="flex items-center gap-2">
          <Bot size={20} />
          <span className="font-semibold">Asistente {tenantName}</span>
        </div>
        <button
          onClick={toggleChat}
          className="p-1 hover:bg-blue-700 rounded-md transition-colors"
          aria-label="Cerrar chat"
        >
          <X size={20} />
        </button>
      </div>

      <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
        <p className="text-xs text-amber-800 dark:text-amber-200">
          ⚠️ <strong>Beta:</strong> Asistente en entrenamiento. Preguntas limitadas.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-800">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 dark:text-gray-400 mt-20">
            <Bot size={48} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">¡Hola! Soy el asistente de {tenantName}.</p>
            <p className="text-sm mt-1">Puedo ayudarte a consultar:</p>
            <ul className="text-sm mt-2 space-y-1 text-left max-w-[200px] mx-auto">
              <li>• Estadísticas de pagos</li>
              <li>• Detalles de suscriptores</li>
              <li>• KPIs y métricas</li>
              <li>• Cancelar suscripciones</li>
            </ul>
          </div>
        )}

        {messages.map((msg, index) => (
          <div
            key={index}
            className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role === 'assistant' && (
              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                <Bot size={16} className="text-blue-600 dark:text-blue-400" />
              </div>
            )}
            <div
              className={`max-w-[75%] rounded-lg px-3 py-2 ${msg.role === 'user'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-600'
                }`}
            >
              <div className="text-sm whitespace-pre-wrap">
                <ReactMarkdown>{msg.content}</ReactMarkdown>
              </div>
              {msg.timestamp && (
                <div
                  className={`text-xs mt-1 ${msg.role === 'user' ? 'text-blue-200' : 'text-gray-400'
                    }`}
                >
                  {new Date(msg.timestamp).toLocaleTimeString('es-CL', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              )}
            </div>
            {msg.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <User size={16} className="text-white" />
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-2 justify-start">
            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
              <Bot size={16} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div className="bg-white dark:bg-gray-700 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-600">
              <Loader2 size={20} className="animate-spin text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Escribe tu mensaje..."
            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-sm"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-lg transition-colors disabled:cursor-not-allowed"
          >
            <Send size={18} />
          </button>
        </div>
      </form>
    </div>
  );
};